import { CheckCircle2, Award, Users, MapPin } from "lucide-react";

const ABOUT_IMAGE =
    "https://images.unsplash.com/photo-1694521787162-5373b598945c";

const STATS = [
    { id: "founded", k: "Founded", v: "2002" },
    { id: "fleet", k: "Cranes", v: "20+" },
    { id: "crew", k: "Crew", v: "60+" },
    { id: "regions", k: "Regions", v: "MH" },
];

const PILLARS = [
    {
        id: "safety",
        Icon: CheckCircle2,
        title: "Safety First",
        body: "Every lift starts with a method statement, load chart review and pre-job inspection. Zero shortcuts.",
    },
    {
        id: "trained",
        Icon: Users,
        title: "Trained Crews",
        body: "Licensed operators, riggers and signal-men with thousands of project hours under their belts.",
    },
    {
        id: "reach",
        Icon: MapPin,
        title: "Local Reach",
        body: "Strategically based to serve Mumbai, Thane, Bhiwandi and surrounding industrial corridors.",
    },
];

export const About = () => {
    return (
        <section
            id="about"
            data-testid="about-section"
            className="relative py-24 lg:py-32 bg-[#0A0A0A] overflow-hidden"
        >
            {/* Background big text */}
            <div
                aria-hidden
                className="pointer-events-none absolute -top-10 -right-10 font-display text-[18vw] leading-none text-white/[0.025] uppercase select-none"
            >
                Surya
            </div>

            <div className="relative max-w-7xl mx-auto px-5 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-start">
                {/* Image side */}
                <div className="reveal lg:col-span-5 relative">
                    <div className="relative aspect-[4/5] overflow-hidden border border-white/10">
                        <img
                            src={ABOUT_IMAGE}
                            alt="Construction crew focused on safety"
                            loading="lazy"
                            className="w-full h-full object-cover"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />
                        {/* Founded badge */}
                        <div className="absolute bottom-5 left-5 right-5 flex items-end justify-between">
                            <div>
                                <div className="font-body text-xs uppercase tracking-[0.32em] text-[#FFC107]">
                                    Since
                                </div>
                                <div className="font-display text-7xl text-white leading-none">
                                    2002
                                </div>
                            </div>
                            <Award
                                size={48}
                                strokeWidth={1.4}
                                className="text-[#FFC107]"
                            />
                        </div>
                    </div>
                    {/* Yellow accent block */}
                    <div className="hidden md:block absolute -bottom-6 -right-6 w-32 h-32 bg-[#FFC107]" />
                </div>

                {/* Text side */}
                <div className="lg:col-span-7 lg:pl-8">
                    <div className="reveal flex items-center gap-3 mb-5">
                        <span className="w-10 h-[2px] bg-[#FFC107]" />
                        <span className="font-body text-xs uppercase tracking-[0.32em] text-[#FFC107]">
                            About Us
                        </span>
                    </div>

                    <h2
                        className="reveal font-display text-5xl sm:text-6xl lg:text-7xl uppercase text-white leading-[0.92]"
                        style={{ animationDelay: "0.1s" }}
                    >
                        Two Decades.
                        <br />
                        Thousands of <span className="text-[#FFC107]">Lifts.</span>
                        <br />
                        <span className="text-neutral-500">One Standard.</span>
                    </h2>

                    <p
                        className="reveal mt-7 font-body text-base lg:text-lg text-neutral-300 leading-relaxed max-w-2xl"
                        style={{ animationDelay: "0.2s" }}
                    >
                        Surya Crane Service was founded in 2002 with a simple
                        idea — be the crew clients call when the lift{" "}
                        <span className="text-white font-semibold">
                            absolutely cannot fail
                        </span>
                        . Today, we operate one of Maharashtra&apos;s most
                        reliable fleets, partnering with construction firms,
                        manufacturers, refineries and infrastructure projects
                        across Mumbai, Thane, Bhiwandi &amp; Wada.
                    </p>
                    <p
                        className="reveal mt-5 font-body text-base text-neutral-400 leading-relaxed max-w-2xl"
                        style={{ animationDelay: "0.3s" }}
                    >
                        We&apos;re obsessive about safety, on-time arrival and
                        the kind of operator skill that turns difficult
                        rigging puzzles into smooth, single-shift jobs.
                    </p>

                    {/* Pillars */}
                    <div className="reveal mt-10 grid grid-cols-1 sm:grid-cols-3 gap-4">
                        {PILLARS.map(({ id, Icon, title, body }, idx) => (
                            <div
                                key={id}
                                data-testid={`about-pillar-${id}`}
                                className="bg-[#171717] border border-white/10 p-5 hover:border-[#FFC107]/40 transition-colors"
                                style={{ animationDelay: `${0.4 + idx * 0.08}s` }}
                            >
                                <Icon
                                    size={22}
                                    strokeWidth={1.8}
                                    className="text-[#FFC107] mb-3"
                                />
                                <div className="font-display text-xl uppercase text-white tracking-wide mb-1.5">
                                    {title}
                                </div>
                                <p className="font-body text-xs text-neutral-400 leading-relaxed">
                                    {body}
                                </p>
                            </div>
                        ))}
                    </div>

                    {/* Mini stats */}
                    <div className="reveal mt-10 grid grid-cols-4 gap-3 border-t border-white/10 pt-8">
                        {STATS.map((s) => (
                            <div key={s.id} data-testid={`about-stat-${s.id}`}>
                                <div className="font-display text-3xl md:text-4xl text-white leading-none">
                                    {s.v}
                                </div>
                                <div className="font-body text-[10px] md:text-xs uppercase tracking-[0.2em] text-neutral-500 mt-2">
                                    {s.k}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </section>
    );
};
